
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/auth";
import LoginForm from "@/components/auth/LoginForm";
import RegisterForm from "@/components/auth/RegisterForm";
import { logError } from "@/utils/errorLogger";

const Auth = () => {
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const [initError, setInitError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(true);

  // Redirect if already logged in - ensure this happens with a small delay
  useEffect(() => {
    try {
      console.log("Auth page mounted, auth state:", { user, isLoading });
      
      // Don't do anything until auth is loaded
      if (isLoading) {
        console.log("Auth is still loading, waiting...");
        return;
      }
      
      setIsProcessing(false);
      
      if (user) {
        console.log('Auth page: User is logged in, redirecting to dashboard');
        const timer = setTimeout(() => {
          navigate("/dashboard");
        }, 300); // Add a small delay to ensure everything is loaded
        
        return () => clearTimeout(timer);
      }
    } catch (error: any) {
      console.error('Error in Auth page redirect:', error);
      setInitError('Failed to process authentication. Please refresh the page.');
      setIsProcessing(false);
      logError({
        type: 'other',
        message: `Auth page redirect error: ${error.message}`,
        stack: error.stack
      });
    }
  }, [user, navigate, isLoading]);

  // Show loading spinner while authentication state is being determined
  if (isLoading || isProcessing) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Show error message if initialization failed
  if (initError) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <div className="text-center p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <h2 className="text-xl font-bold text-red-600 dark:text-red-400 mb-4">Authentication Error</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">{initError}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  // Don't render login form if user is already logged in
  if (user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
        <p className="text-center text-gray-600 dark:text-gray-300">Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-bold">SupplyMate</CardTitle>
          <CardDescription>
            Manage your medical supplies efficiently
          </CardDescription>
        </CardHeader>
        
        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>
          
          <TabsContent value="login">
            <LoginForm />
          </TabsContent>
          
          <TabsContent value="register">
            <RegisterForm />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Auth;
